import { NavLink, useNavigate } from 'react-router-dom';
import { useApp } from '@/contexts/AppContext';
import { useQuestions } from '@/contexts/QuestionsContext';
import { useProject } from '@/contexts/ProjectContext';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import ThemeToggle from '@/components/ThemeToggle';
import { ChevronDown, Key, LogOut } from 'lucide-react';

const navTabClass = ({ isActive }: { isActive: boolean }) =>
  `flex items-center h-full px-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
    isActive
      ? 'border-primary text-foreground'
      : 'border-transparent text-muted-foreground hover:text-foreground'
  }`;

export default function TopBar() {
  const { user, logout } = useAuth();
  const { resetAppState } = useApp();
  const { resetQuestions } = useQuestions();
  const { clearProject } = useProject();
  const navigate = useNavigate();

  if (!user) return null;

  const canManageTokens = user.roles?.includes('especial');

  const handleProjectsClick = (e: React.MouseEvent) => {
    e.preventDefault();
    resetAppState();
    resetQuestions();
    clearProject();
    navigate('/projects');
  };

  return (
    <header className="sticky top-0 z-50 border-b bg-card">
      <div className="container flex items-center h-14 gap-6">
        <div className="flex items-center gap-2 shrink-0">
          <img src="/logo.png" alt="SPICE Logo" className="h-8 w-auto" />
          <span className="text-base font-semibold tracking-tight">
            SPICE
          </span>
        </div>

        <nav aria-label="Main navigation" className="flex items-stretch h-full">
          <NavLink
            to="/projects"
            className={navTabClass}
            onClick={handleProjectsClick}
          >
            Projects
          </NavLink>
        </nav>

        <div className="ml-auto flex items-center gap-2">
          <ThemeToggle />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="gap-1 border-l rounded-none pl-3 ml-1">
                <span className="text-sm">{user.username}</span>
                <ChevronDown className="h-3.5 w-3.5 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel>{user.username}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {canManageTokens && (
                <DropdownMenuItem onClick={() => navigate('/tokens')}>
                  <Key className="mr-2 h-4 w-4" />
                  API Tokens
                </DropdownMenuItem>
              )}
              {canManageTokens && <DropdownMenuSeparator />}
              <DropdownMenuItem onClick={() => logout()}>
                <LogOut className="mr-2 h-4 w-4" />
                Logout
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <div className="px-2 py-1.5 text-xs text-muted-foreground">
                v{__APP_VERSION__} · {__APP_COMMIT__}
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
